// @ts-nocheck
import { DashboardLayout } from "./Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Building2, Edit, Trash2, Eye, X } from "lucide-react";
import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/contexts/use-auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { useDebouncedInput } from "@/hooks/useDebouncedInput";
import { useOrg } from "@/contexts/useOrg";
import AxiosInstance from "@/utils/AxiosInstance";
import {
  getErrToast,
  getPromise,
  getPromiseToast,
  formatOrganizationTimezoneDisplay,
} from "@/utils/helpers";
import LoaderWrapper from "./LoaderWrapper";
import { HorizontalCardShimmer } from "@/components/Shimmer";
import { useGetData } from "@/hooks/useGetData";
import OrganizationForm from "@/components/OrganizationForm";
import { is } from "date-fns/locale";
import TableWithSearch from "@/components/TableWithSearch";
import { caretakerTableHeaders, organizationHeaders } from "@/utils/tablesData";
import { useNavigate } from "react-router";

// Dummy data for organizations
const dummyOrganizations = [
  {
    _id: "anthro-care",
    name: "Anthro Care",
    email: "info@anthrocare.com",
    description: "Central organization for Anthro Care",
    phone: "555-0000",
    pocName: "Admin User",
    pocPhone: "555-0100",
    hasOutpatients: true,
  },
  {
    _id: "org2",
    name: "City Hospital",
    email: "admin@cityhospital.com",
    description: "Large city hospital providing comprehensive care",
    phone: "555-0200",
    pocName: "Jane Smith",
    pocPhone: "555-0300",
    hasOutpatients: false,
  },
  {
    _id: "org3",
    name: "Community Clinic",
    email: "contact@communityclinic.org",
    description: "Local clinic serving the community",
    phone: "555-0400",
    pocName: "Bob Johnson",
    pocPhone: "555-0500",
    hasOutpatients: true,
  },
];

// Dummy data for facilities
const dummyFacilities = [
  {
    _id: "fac1",
    organizationId: "anthro-care",
    name: "Main Inpatient Ward",
    address: "123 Health St",
    city: "New York",
    state: "NY",
    pocName: "Ward Supervisor",
    pocPhone: "555-0600",
    isOutpatients: false,
  },
  {
    _id: "fac2",
    organizationId: "anthro-care",
    name: "Outpatient Services",
    address: "456 Care Ave",
    city: "New York",
    state: "NY",
    pocName: "",
    pocPhone: "",
    isOutpatients: true,
  },
  {
    _id: "fac3",
    organizationId: "org2",
    name: "Emergency Department",
    address: "789 Emergency Rd",
    city: "Boston",
    state: "MA",
    pocName: "Emergency Coordinator",
    pocPhone: "555-0700",
    isOutpatients: false,
  },
  {
    _id: "fac4",
    organizationId: "org3",
    name: "General Practice",
    address: "101 Community Ln",
    city: "Los Angeles",
    state: "CA",
    pocName: "Practice Manager",
    pocPhone: "555-0800",
    isOutpatients: true,
  },
];

export default function Organizations() {
  const { user, hasModuleAdminAccess } = useAuth();
  const nav= useNavigate();
  const {
    input: search,
    setInput: setSearch,
    debouncedInput,
  } = useDebouncedInput("", 500);

  const { allFacilities, setOrganizations: setOrgUniversal } = useOrg();
  const [btnLoading, setBtnLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const { loading: orgLoading, err: orgError } = useGetData(
    "/organizations",
    { search: debouncedInput },
    false,
    true,
    (err, data) => {
      if (data && !err) {
        setOrganizations(data?.data?.organizations);
      }
    },
  );
  // const organizations = apiData?.data?.organizations;

  const [selectedOrg, setSelectedOrg] = useState<any>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [numberOfFacilities, setNumberOfFacilities] = useState(1);
  const [facilities, setFacilities] = useState<
    Array<{
      name: string;
      address: string;
      city: string;
      state: string;
      pocName: string;
      pocPhone: string;
    }>
  >([
    { name: "", address: "", city: "", state: "", pocName: "", pocPhone: "" },
  ]);
  const [newFacilities, setNewFacilities] = useState<
    Array<{
      name: string;
      address: string;
      city: string;
      state: string;
      pocName: string;
      pocPhone: string;
    }>
  >([]);

  // const [organizations, setOrganizations] = useState(dummyOrganizations);
  // const [allFacilities, setAllFacilities] = useState(dummyFacilities);

  // TODO: Replace with actual API call to fetch organizations
  // e.g., const organizations = await fetchJSON('/api/organizations');
  // const editOrgFacilities = selectedOrg ? allFacilities.filter((f: any) => f.organizationId === selectedOrg._id) : [];
  const editOrgFacilities = selectedOrg ? selectedOrg?.facilities : [];

  // TODO: Replace with actual API call to fetch facilities for organization
  // e.g., const editOrgFacilities = await fetchJSON(`/api/organizations/${selectedOrg?._id}/facilities`);

  // Check if user is admin in master org (Anthro Care)
  // Admin users in the "Anthro Care" organization (or when selectedOrgId is "anthro-care") can edit
  const selectedOrgId = localStorage.getItem("selectedOrgId");
  const isAdminInMasterOrg =
    user?.role === "admin" && selectedOrgId === "anthro-care";
  const canEditOrganizations =
    isAdminInMasterOrg || hasModuleAdminAccess("canAccessOrganizations");

  const handleViewOrganization = (org: any) => {
    setSelectedOrg(org);
    setNewFacilities([]);
    setIsEditDialogOpen(true);
  };

  const handleFacilityCountChange = (value: number[]) => {
    const count = value[0];
    setNumberOfFacilities(count);
    const newFacilities = Array(count)
      .fill(null)
      .map(
        (_, i) =>
          facilities[i] || {
            name: "",
            address: "",
            city: "",
            state: "",
            pocName: "",
            pocPhone: "",
          },
      );
    setFacilities(newFacilities);
  };

  const handleFacilityChange = (
    index: number,
    field: string,
    value: string,
  ) => {
    const newFacilities = [...facilities];
    newFacilities[index] = { ...newFacilities[index], [field]: value };
    setFacilities(newFacilities);
  };

  const handleCreateOrganization = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    try {
      e.preventDefault();

      if (!canEditOrganizations) {
        toast.error("Only administrators in Anthro Care can add organizations");
        return;
      }

      const formData = new FormData(e.currentTarget);

      // Validate facilities
      for (const facility of facilities) {
        if (
          !facility.address.trim() ||
          !facility.city.trim() ||
          !facility.state.trim()
        ) {
          toast.error("Please fill in all facility details");
          return;
        }
      }

      // TODO: Replace with actual API call to create organization
      // e.g., await fetchJSON('/api/organizations', { method: 'POST', body: JSON.stringify({ name: ..., ... }) });
      // For simulation with dummy data:
      const newOrgId = Date.now().toString();
      const newOrg = {
        // _id: newOrgId,
        name: formData.get("name") as string,
        description: (formData.get("description") as string) || "",
        email: formData.get("email") as string,
        phone: (formData.get("phone") as string) || "3475745745",
        address: (formData.get("address") as string) || "3temp address",

        pocName: (formData.get("pocName") as string) || "",
        pocPhone: (formData.get("pocPhone") as string) || "",
        hasOutpatients: formData.get("hasOutpatients") === "on",
      };

      const {
        data: { data },
      } = await AxiosInstance().post("/organizations", newOrg);
      console.log(data);
      setOrganizations((p) => [data, ...p]);
      setOrgUniversal((p) => [...p, data]);
      // // Add facilities
      // facilities.forEach((fac, i) => {
      //   if (fac.address.trim() && fac.city.trim() && fac.state.trim()) {
      //     const newFacId = `${newOrgId}-${i}`;
      //     setAllFacilities((prev) => [...prev, {
      //       _id: newFacId,
      //       organizationId: newOrgId,
      //       name: fac.name,
      //       address: fac.address,
      //       city: fac.city,
      //       state: fac.state,
      //       pocName: fac.pocName,
      //       pocPhone: fac.pocPhone,
      //       isOutpatients: false,
      //     }]);
      //   }
      // });

      toast.success("Organization added successfully");
      setIsDialogOpen(false);
      setNumberOfFacilities(1);
      setFacilities([
        {
          name: "",
          address: "",
          city: "",
          state: "",
          pocName: "",
          pocPhone: "",
        },
      ]);
    } catch (error) {
      getErrToast(error);
    }
  };

  const handleUpdateOrganization = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();

    if (!canEditOrganizations) {
      toast.error(
        "Only administrators in Anthro Care can update organizations",
      );
      return;
    }

    const formData = new FormData(e.currentTarget);

    if (!selectedOrg) return;

    // TODO: Replace with actual API call to update organization
    // e.g., await fetchJSON(`/api/organizations/${selectedOrg._id}`, { method: 'PUT', body: JSON.stringify({ name: ..., ... }) });
    // For simulation with dummy data:
    const updatedOrg = {
      // ...selectedOrg,
      name: formData.get("name") as string,
      description:
        (formData.get("description") as string) || selectedOrg.description,
      email: formData.get("email") as string,
      phone: (formData.get("phone") as string) || "3475745745",
      address: (formData.get("address") as string) || "3temp address",
      pocName: (formData.get("pocName") as string) || selectedOrg.pocName,
      pocPhone: (formData.get("pocPhone") as string) || selectedOrg.pocPhone,
      hasOutpatients: formData.get("hasOutpatients") === "on",
    };
    const {
      data: { data },
    } = await AxiosInstance().put(
      "/organizations/" + selectedOrg?._id,
      updatedOrg,
    );
    setOrganizations((prev) =>
      prev.map((o) => (o._id === selectedOrg._id ? data : o)),
    );
    setOrgUniversal((prev) =>
      prev.map((o) => (o._id === selectedOrg._id ? data : o)),
    );

    // Add any new facilities
    if (newFacilities.length > 0) {
      for (const facility of newFacilities) {
        if (
          !facility.name.trim() ||
          !facility.address.trim() ||
          !facility.city.trim() ||
          !facility.state.trim()
        ) {
          toast.error("Please fill in all required facility fields");
          return;
        }

        // TODO: Replace with actual API call to add facility
        // e.g., await fetchJSON(`/api/organizations/${selectedOrg._id}/facilities`, { method: 'POST', body: JSON.stringify({ ...facility }) });
        // For simulation with dummy data:
        const newFacId = `${selectedOrg._id}-${Date.now()}-${newFacilities.indexOf(facility)}`;
        setAllFacilities((prev) => [
          ...prev,
          {
            _id: newFacId,
            organizationId: selectedOrg._id,
            ...facility,
            isOutpatients: false,
          },
        ]);
      }
    }

    toast.success("Organization updated successfully");
    setIsEditDialogOpen(false);
    setSelectedOrg(null);
    setNewFacilities([]);
  };

  const handleUpdateFacility = async (facilityId: string, updates: any) => {
    if (!canEditOrganizations) {
      toast.error("Only administrators in Anthro Care can update facilities");
      return;
    }

    // TODO: Replace with actual API call to update facility
    // e.g., await fetchJSON(`/api/facilities/${facilityId}`, { method: 'PUT', body: JSON.stringify(updates) });
    // For simulation with dummy data:
    setAllFacilities((prev) =>
      prev.map((f: any) => (f._id === facilityId ? { ...f, ...updates } : f)),
    );

    toast.success("Facility updated successfully");
  };

  const handleAddNewFacilityForm = () => {
    if (!canEditOrganizations) {
      toast.error("Only administrators in Anthro Care can add facilities");
      return;
    }
    setNewFacilities([
      ...newFacilities,
      { name: "", address: "", city: "", state: "", pocName: "", pocPhone: "" },
    ]);
  };

  const handleRemoveNewFacility = (index: number) => {
    setNewFacilities(newFacilities.filter((_, i) => i !== index));
  };

  const handleNewFacilityChange = (
    index: number,
    field: string,
    value: string,
  ) => {
    const updated = [...newFacilities];
    updated[index] = { ...updated[index], [field]: value };
    setNewFacilities(updated);
  };

  const handleAddNewFacility = async (facility: any) => {
    if (!selectedOrg) return;

    if (
      !facility.name.trim() ||
      !facility.address.trim() ||
      !facility.city.trim() ||
      !facility.state.trim()
    ) {
      toast.error("Please fill in all required facility fields");
      return;
    }

    // TODO: Replace with actual API call to add facility
    // e.g., await fetchJSON(`/api/organizations/${selectedOrg._id}/facilities`, { method: 'POST', body: JSON.stringify(facility) });
    // For simulation with dummy data:
    const newFacId = `${selectedOrg._id}-${Date.now()}`;
    setAllFacilities((prev) => [
      ...prev,
      {
        _id: newFacId,
        organizationId: selectedOrg._id,
        ...facility,
        isOutpatients: false,
      },
    ]);

    toast.success("Facility added successfully");
    // Remove this facility from the newFacilities array
    setNewFacilities(newFacilities.filter((f) => f !== facility));
  };
  function modifyOrg(newOrg, isUpdate) {
    if (isUpdate) {
      setOrganizations((p) =>
        Array.isArray(p)
          ? p.map((org) => (org?._id == newOrg?._id ? newOrg : org))
          : [newOrg],
      );
      setOrgUniversal((p) =>
        Array.isArray(p)
          ? p.map((org) => (org?._id == newOrg?._id ? newOrg : org))
          : [newOrg],
      );
    } else {
      setOrganizations((p) => (Array.isArray(p) ? [newOrg, ...p] : [newOrg]));
      setOrgUniversal((p) => (Array.isArray(p) ? [newOrg, ...p] : [newOrg]));
    }
  }
  // console.log({ selectedOrg });
  const handleAddUpdateOrg = async (formData, cb = () => {}, step, isNew) => {
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    const payload = {
      ...formData,
      zipCode: formData?.zipCode ?? "",
      billerZipCode: formData?.billerZipCode ?? "",
      billingZipCode: formData?.billingZipCode ?? "",
      step,
    };

    try {
      setBtnLoading(true);
      let data = {};
      if (isNew && !selectedOrg?._id) {
        console.log("POST");
        const res = await AxiosInstance().post("/organizations", payload);
        data = res?.data?.data?._id
          ? { ...formData, ...res?.data?.data }
          : formData;
        setSelectedOrg(res?.data?.data);
        modifyOrg(data, false);
      } else {
        console.log("PUT");

        const res = await AxiosInstance().put(
          "/organizations/" + selectedOrg?._id,
          payload,
        );
        data = { ...selectedOrg, ...formData };
        modifyOrg(data, true);
        // step == 3 && toast.success("Organization updated succcessfully");
      }
      if (step == 3) {
        resolveFn(
          !isNew
            ? "Organization updated succcessfully"
            : "Organization created succcessfully",
        );
      } else {
        resolveFn("Data Saved");
      }

      console.log("UPDATED ORG", data);
      cb();
    } catch (error) {
      console.log(error.message);
      rejectFn(error);
      throw error;
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <DashboardLayout activePage="/organizations" rightcomponent={<>
              {canEditOrganizations && (
            <Dialog
              open={isDialogOpen}
              onOpenChange={(open) => {
                setIsDialogOpen(open);
                !open && setSelectedOrg({});
              }}
            >
              <DialogTrigger asChild>
                <Button className="w-auto">
                  <Plus className="w-4 h-4 " />
                   Organization
                </Button>
              </DialogTrigger>
              <OrganizationForm
                defaultValues={{}}
                isOpen={isDialogOpen}
                heading="Add Organization"
                btnLoading={btnLoading}
                onClose={() => {
                  setIsDialogOpen(false);
                  setSelectedOrg({});
                }}
                onSubmit={handleAddUpdateOrg}
              />
            </Dialog>
          )}
    </>}>
      <div className="">
  

        {/* Edit Organization Dialog */}
        <Dialog
          open={isEditDialogOpen}
          onOpenChange={(open) => {
            setIsEditDialogOpen(open);
            !open && setSelectedOrg({});
          }}
        >
          <OrganizationForm
            key={selectedOrg?._id}
            defaultValues={selectedOrg}
            isOpen={isDialogOpen}
            onClose={() => {
              setIsEditDialogOpen(false);
              setSelectedOrg({});
            }}
            heading={
              canEditOrganizations ? "Edit Organizations " : "View Organization"
            }
            btnLoading={btnLoading}
            onSubmit={handleAddUpdateOrg}
          />
        </Dialog>

        {/* <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <CardTitle>Organization List</CardTitle>
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search organizations..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 pr-9"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-3 top-3 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <LoaderWrapper
              loading={orgLoading}
              error={orgError}
              customLoaderComponent={<HorizontalCardShimmer count={4} />}
            >
              {organizations && organizations.length > 0 ? (
                <div className="space-y-4">
                  {organizations.map((org, index) => (
                    <motion.div
                      key={org._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-6 border rounded-lg hover:shadow-md transition-all"
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="space-y-3 flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-lg font-semibold flex items-center gap-2">
                                <Building2 className="w-5 h-5" />
                                {org.organizationName}
                              </h3>
                              <p className="text-sm text-muted-foreground mt-1">
                                {org.email}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewOrganization(org)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            View Details
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {search
                      ? "No organizations found matching your criteria"
                      : "No organizations yet. Add your first organization to get started."}
                  </p>
                </div>
              )}
            </LoaderWrapper>
          </CardContent>
        </Card> */}

        <TableWithSearch
          fetchCondition={true}
          
          // heading="Organizations List"
          isIncludeSearch
          isHideSelect
          isColumnSelection
          columnPreferenceKey="organizations-list"
          clickRow={() => {}}
          apiDataKey="organizations"
          columnHeaders={organizationHeaders}
          searchPlaceholder="Search Organizations ... "
          url="/organizations"
          params={{}}
          data={organizations}
          viewData={organizations.map((org) => {
            const tzRaw = org?.timezone ?? org?.timeZone;
            return {
              ...org,
              ownerFirstName: `${org?.ownerFirstName || ""} ${org?.ownerMiddleName || ""} ${org?.ownerLastName || ""}`,
              timezone: formatOrganizationTimezoneDisplay(tzRaw),
            };
          })}
          setData={setOrganizations}
          isActionColumn
          handleAction={(org) => {
            handleViewOrganization(org)
            ;
          }}
          // handleEditAction={(org) => {
          //   handleViewOrganization(org);
          // }}
            // clickRow={(data:any)=> nav(data?._id,{state: {data}})}
            moreActions={[
              {
                label : "View ",
                icon: <Eye/>,
                isShow:()=> true,
                onClick: (data)=> nav(`/organizations/${data?._id}`, {state: {data: data}})
              }
            ]}
        />
      </div>
    </DashboardLayout>
  );
}
